import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Edit2, Trash2, AlertCircle, CheckCircle2, PauseCircle } from 'lucide-react';
import { api, ApiClientError } from '@/api/client';
import type { Tenant } from '@/types';

export function TenantDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingUser, setTogglingUser] = useState<number | null>(null);
  const isSuperAdmin = api.isSuperAdmin();

  function load() {
    if (!id) return;
    setLoading(true);
    api.getTenant(id)
      .then(setTenant)
      .catch((e) => setError(e instanceof ApiClientError ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }

  useEffect(load, [id]);

  async function toggleStatus() {
    if (!tenant) return;
    setToggling(true);
    try {
      const updated = await api.updateTenant(tenant.tenant_id, {
        status: tenant.status === 'active' ? 'suspended' : 'active',
      });
      setTenant(updated);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Update failed');
    } finally {
      setToggling(false);
    }
  }

  async function toggleUserStatus(userId: number, currentStatus: string) {
    if (!tenant) return;
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    setTogglingUser(userId);
    try {
      await api.updateTenantUser(tenant.tenant_id, userId, newStatus);
      load();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Update failed');
    } finally {
      setTogglingUser(null);
    }
  }

  async function handleDelete() {
    if (!tenant || !confirm(`Delete tenant "${tenant.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.deleteTenant(tenant.tenant_id);
      navigate('/tenants');
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Delete failed');
      setDeleting(false);
    }
  }

  if (loading) return <div className="text-sm text-gray-500 dark:text-gray-400 py-12 text-center">Loading…</div>;
  if (error) return (
    <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-800 dark:text-red-400 text-sm max-w-lg">
      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />{error}
    </div>
  );
  if (!tenant) return null;

  const quotaPerUser = (tenant.users?.length ?? tenant.user_count ?? 0) > 0
    ? Math.floor(tenant.max_requests_per_day / (tenant.users?.length ?? tenant.user_count ?? 1))
    : tenant.max_requests_per_day;

  const monthPct = tenant.max_requests_per_month > 0
    ? Math.round(((tenant.requests_month ?? 0) / tenant.max_requests_per_month) * 100)
    : 0;

  return (
    <div className="max-w-5xl">
      <button
        onClick={() => navigate('/tenants')}
        className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-6"
      >
        <ArrowLeft size={15} /> Tenants
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">{tenant.name}</h2>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <code className="text-xs text-gray-500 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
              {tenant.tenant_id}
            </code>
            {tenant.status === 'active'
              ? <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full"><CheckCircle2 size={11} /> Active</span>
              : <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full"><PauseCircle size={11} /> Suspended</span>
            }
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {isSuperAdmin && (
            <Link to={`/tenants/${tenant.tenant_id}/edit`} className="btn-secondary">
              <Edit2 size={14} className="mr-2" /> Edit
            </Link>
          )}
          <button
            onClick={toggleStatus}
            disabled={toggling}
            className="btn-secondary"
          >
            {toggling ? '…' : tenant.status === 'active' ? 'Suspend' : 'Reactivate'}
          </button>
          {isSuperAdmin && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="btn-secondary text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
            >
              <Trash2 size={14} className="mr-2" />
              {deleting ? '…' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      {/* Stats row — 2 cols on mobile, 4 on sm+ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard label="Users" value={`${tenant.user_count ?? tenant.users?.length ?? 0} / ${tenant.max_users}`} />
        <StatCard label="Quota / User / Day" value={quotaPerUser.toLocaleString()} />
        <StatCard label="Requests Today" value={(tenant.requests_today ?? 0).toLocaleString()} />
        <StatCard
          label="Requests This Month"
          value={`${(tenant.requests_month ?? 0).toLocaleString()} / ${tenant.max_requests_per_month.toLocaleString()}`}
          warn={monthPct >= 80}
        />
      </div>

      {/* Monthly usage bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6">
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-2">
          <span>Monthly usage</span>
          <span>{monthPct}%</span>
        </div>
        <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${monthPct >= 90 ? 'bg-red-500' : monthPct >= 70 ? 'bg-amber-400' : 'bg-brand-500'}`}
            style={{ width: `${Math.min(100, monthPct)}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 mt-1">
          <span>0</span>
          <span>{tenant.max_requests_per_month.toLocaleString()}</span>
        </div>
      </div>

      {/* Users section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Users</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Auto-registered when a token is requested. Quota updates dynamically as users join.
          </p>
        </div>

        {!tenant.users || tenant.users.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 px-5 py-10 text-center">
            No users yet — users appear when the host app calls{' '}
            <code className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">
              POST /api/support/token
            </code>
          </div>
        ) : (
          <>
            {/* ── Mobile: card per user ── */}
            <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
              {tenant.users.map((u) => {
                const effective = u.allocated_daily_quota + (u.rollover_quota ?? 0);
                const todayPct  = effective > 0
                  ? Math.round(((u.requests_today ?? 0) / effective) * 100)
                  : 0;
                return (
                  <div key={u.user_id} className="px-4 py-4 space-y-3">
                    {/* Card header */}
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-sm font-semibold text-gray-900 dark:text-white">
                        #{u.user_id}
                      </span>
                      <div className="flex items-center gap-2">
                        {u.status === 'active'
                          ? <span className="text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full">Active</span>
                          : <span className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 px-2 py-0.5 rounded-full">Suspended</span>
                        }
                        <button
                          onClick={() => toggleUserStatus(u.user_id, u.status)}
                          disabled={togglingUser === u.user_id}
                          className={`text-xs px-2.5 py-1 rounded-md border font-medium transition-colors ${
                            u.status === 'active'
                              ? 'border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30'
                              : 'border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30'
                          } disabled:opacity-50`}
                        >
                          {togglingUser === u.user_id ? '…' : u.status === 'active' ? 'Suspend' : 'Reactivate'}
                        </button>
                      </div>
                    </div>

                    {/* Quota row */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg px-3 py-2.5">
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400">Daily quota</span>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                          {effective.toLocaleString()}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">
                        {u.allocated_daily_quota.toLocaleString()} base
                        {(u.rollover_quota ?? 0) > 0 && (
                          <span className="text-brand-600 dark:text-brand-400 font-medium ml-1">
                            + {(u.rollover_quota ?? 0).toLocaleString()} rollover
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Stats grid */}
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className={`text-base font-semibold tabular-nums ${todayPct >= 80 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>
                          {(u.requests_today ?? 0).toLocaleString()}
                        </div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">Today</div>
                      </div>
                      <div>
                        <div className="text-base font-semibold tabular-nums text-gray-900 dark:text-white">
                          {(u.requests_month ?? 0).toLocaleString()}
                        </div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">This Month</div>
                      </div>
                      <div>
                        <div className="text-base font-semibold tabular-nums text-gray-900 dark:text-white">
                          {((u.tokens_total ?? 0) / 1000).toFixed(1)}k
                        </div>
                        <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">Tokens</div>
                      </div>
                    </div>

                    {/* Last active */}
                    <div className="text-xs text-gray-400 dark:text-gray-500">
                      Last active: {new Date(u.last_active_at).toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Desktop: full table ── */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    <th className="px-5 py-3 text-left">User ID</th>
                    <th className="px-5 py-3 text-left">Status</th>
                    <th className="px-5 py-3 text-right">Base / Day</th>
                    <th className="px-5 py-3 text-right">Rollover</th>
                    <th className="px-5 py-3 text-right">Effective / Day</th>
                    <th className="px-5 py-3 text-right">Today</th>
                    <th className="px-5 py-3 text-right">This Month</th>
                    <th className="px-5 py-3 text-right">Tokens (total)</th>
                    <th className="px-5 py-3 text-left">Last Active</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                  {tenant.users.map((u) => {
                    const todayPct = u.allocated_daily_quota > 0
                      ? Math.round(((u.requests_today ?? 0) / u.allocated_daily_quota) * 100)
                      : 0;
                    return (
                      <tr key={u.user_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-5 py-3 font-mono text-gray-700 dark:text-gray-300">#{u.user_id}</td>
                        <td className="px-5 py-3">
                          {u.status === 'active'
                            ? <span className="text-xs text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 px-2 py-0.5 rounded-full">Active</span>
                            : <span className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 px-2 py-0.5 rounded-full">Suspended</span>
                          }
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{u.allocated_daily_quota.toLocaleString()}</td>
                        <td className="px-5 py-3 text-right tabular-nums">
                          {(u.rollover_quota ?? 0) > 0
                            ? <span className="text-brand-600 dark:text-brand-400 font-medium">+{(u.rollover_quota ?? 0).toLocaleString()}</span>
                            : <span className="text-gray-400 dark:text-gray-500">—</span>
                          }
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums font-semibold text-gray-900 dark:text-white">
                          {(u.allocated_daily_quota + (u.rollover_quota ?? 0)).toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums">
                          <span className={todayPct >= 80 ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}>
                            {(u.requests_today ?? 0).toLocaleString()}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{(u.requests_month ?? 0).toLocaleString()}</td>
                        <td className="px-5 py-3 text-right tabular-nums text-gray-500 dark:text-gray-400">{(u.tokens_total ?? 0).toLocaleString()}</td>
                        <td className="px-5 py-3 text-xs text-gray-500 dark:text-gray-400">
                          {new Date(u.last_active_at).toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            onClick={() => toggleUserStatus(u.user_id, u.status)}
                            disabled={togglingUser === u.user_id}
                            className={`text-xs px-3 py-1 rounded-md border font-medium transition-colors ${
                              u.status === 'active'
                                ? 'border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30'
                                : 'border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {togglingUser === u.user_id ? '…' : u.status === 'active' ? 'Suspend' : 'Reactivate'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className={`rounded-xl border p-4 ${warn ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}>
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</div>
      <div className={`text-xl font-semibold ${warn ? 'text-amber-700 dark:text-amber-400' : 'text-gray-900 dark:text-white'}`}>{value}</div>
    </div>
  );
}
